import React, { useEffect, useState } from "react";

/**
 * "Updated 8s ago" — the page is live, and this is how the reader knows.
 *
 * Without it, auto-refresh is indistinguishable from a page that never updates:
 * the graph simply looks the same, and the user reaches for reload anyway. The
 * stale variant is the other half of that honesty — when a poll fails we keep
 * showing the last good catalog, and saying so is what makes that acceptable.
 */

export interface CabinetFreshnessProps {
	lastUpdatedAt: number | null;
	refreshing: boolean;
	stale: boolean;
	onRefresh: () => void;
}

/** Ticks once a second so the relative label stays true while nothing loads. */
function useNow(enabled: boolean): number {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!enabled) return;
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [enabled]);
	return now;
}

export function formatAge(ageMs: number): string {
	const seconds = Math.max(0, Math.round(ageMs / 1000));
	if (seconds < 5) return "just now";
	if (seconds < 60) return `${seconds}s ago`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	return `${Math.floor(minutes / 60)}h ago`;
}

const CabinetFreshness: React.FC<CabinetFreshnessProps> = ({
	lastUpdatedAt,
	refreshing,
	stale,
	onRefresh,
}) => {
	const now = useNow(lastUpdatedAt !== null);
	const label =
		lastUpdatedAt === null ? "" : formatAge(now - lastUpdatedAt);

	return (
		<div className="flex items-center gap-2 text-xs text-text-muted">
			<span
				aria-hidden="true"
				className={`inline-block h-1.5 w-1.5 rounded-full ${
					stale
						? "bg-amber-500"
						: refreshing
							? "animate-pulse bg-teal-500"
							: "bg-emerald-500"
				}`}
			/>
			<span data-testid="cabinet-freshness">
				{stale
					? `Showing last known catalog — refresh failed${
							label ? ` (updated ${label})` : ""
						}`
					: refreshing && lastUpdatedAt === null
						? "Loading…"
						: label
							? `Updated ${label}`
							: ""}
			</span>
			<button
				type="button"
				onClick={onRefresh}
				disabled={refreshing}
				className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:bg-gray-100 disabled:opacity-50"
			>
				Refresh
			</button>
		</div>
	);
};

export default CabinetFreshness;
